import { useState, useEffect } from "react";
import { useToast } from "@/contexts/ToastContext";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Clock,
  CheckCircle2,
  Loader2,
  XCircle,
  Search,
  BookMarked,
  AlertTriangle,
} from "lucide-react";
import { circulationApi, type HoldRecord, type PaginatedResponse } from "@/lib/api/circulation";

const statusConfig: Record<string, { label: string; cls: string; icon: React.ElementType }> = {
  pending: { label: "Pending", cls: "bg-yellow-100 text-yellow-700", icon: Clock },
  ready: { label: "Ready for Pickup", cls: "bg-blue-100 text-blue-700", icon: BookMarked },
  fulfilled: { label: "Fulfilled", cls: "bg-green-100 text-green-700", icon: CheckCircle2 },
  cancelled: { label: "Cancelled", cls: "bg-gray-100 text-gray-500", icon: XCircle },
  expired: { label: "Expired", cls: "bg-red-100 text-red-600", icon: AlertTriangle },
};

type TabFilter = "pending" | "ready" | "all";

const HoldsPage = () => {
  const toast = useToast();
  const [holds, setHolds] = useState<PaginatedResponse<HoldRecord> | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TabFilter>("pending");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [cancelId, setCancelId] = useState<string | null>(null);

  useEffect(() => {
    loadHolds();
  }, [tab, page]);

  const loadHolds = async () => {
    setLoading(true);
    try {
      const status = tab === "all" ? undefined : tab;
      const result = await circulationApi.getHolds({ status, page, limit: 30 });
      setHolds(result);
    } catch (err: any) {
      toast.error("Failed to load holds");
    } finally {
      setLoading(false);
    }
  };

  const handleFulfill = async (holdId: string) => {
    setActionLoading(holdId);
    try {
      await circulationApi.fulfillHold(holdId);
      toast.success("Hold marked as ready for pickup");
      loadHolds();
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Failed to fulfill hold");
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async () => {
    if (!cancelId) return;
    setActionLoading(cancelId);
    try {
      await circulationApi.cancelHold(cancelId);
      toast.success("Hold cancelled");
      setCancelId(null);
      loadHolds();
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Failed to cancel hold");
    } finally {
      setActionLoading(null);
    }
  };

  const getTitle = (hold: HoldRecord) =>
    hold.item?.biblio?.title ?? hold.biblio?.title ?? "Unknown Title";

  const filteredData = holds?.data?.filter((h) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      getTitle(h).toLowerCase().includes(q) ||
      h.user?.firstName?.toLowerCase().includes(q) ||
      h.user?.lastName?.toLowerCase().includes(q) ||
      h.user?.email?.toLowerCase().includes(q)
    );
  });

  return (
    <AppLayout>
      <PageContainer width="wide">
        <PageHeader
          title="Holds & Reserves"
          description="Manage hold queue, fulfill, and cancel holds"
          breadcrumbItems={[
            { label: "Dashboard", href: "/dashboard" },
            { label: "Circulation", href: "/circulation" },
            { label: "Holds & Reserves" },
          ]}
          showSearch={false}
          titleSize="md"
        />

        {/* Tabs */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex bg-muted/50 rounded-lg p-1 gap-1">
            {(["pending", "ready", "all"] as TabFilter[]).map((t) => (
              <button
                key={t}
                onClick={() => {
                  setTab(t);
                  setPage(1);
                }}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  tab === t
                    ? "bg-background shadow font-medium"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {t === "all" ? "All" : t === "pending" ? "Pending" : "Ready for Pickup"}
              </button>
            ))}
          </div>
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by title, patron..."
              className="pl-10"
            />
          </div>
        </div>

        {/* Holds list */}
        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : !filteredData || filteredData.length === 0 ? (
          <Card className="p-8 text-center text-muted-foreground">
            <Clock className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p>No holds found</p>
          </Card>
        ) : (
          <div className="space-y-2">
            {filteredData.map((hold) => {
              const cfg = statusConfig[hold.status] ?? statusConfig.pending;
              const StatusIcon = cfg.icon;
              return (
                <Card key={hold.id} className="p-4 flex items-center gap-4">
                  <div className={`p-2 rounded-lg ${cfg.cls}`}>
                    <StatusIcon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{getTitle(hold)}</p>
                    <p className="text-xs text-muted-foreground">
                      {hold.user?.firstName} {hold.user?.lastName} ({hold.user?.email})
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Placed: {new Date(hold.reservedAt).toLocaleDateString()}
                      {hold.pickupBranch ? ` | Pickup: ${hold.pickupBranch.name}` : ""}
                      {hold.expiresAt
                        ? ` | Expires: ${new Date(hold.expiresAt).toLocaleDateString()}`
                        : ""}
                    </p>
                  </div>
                  <Badge variant="secondary" className={`${cfg.cls} text-xs`}>
                    {cfg.label}
                  </Badge>
                  <div className="flex gap-1.5 shrink-0">
                    {hold.status === "pending" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleFulfill(hold.id)}
                        disabled={actionLoading === hold.id}
                      >
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Ready
                      </Button>
                    )}
                    {(hold.status === "pending" || hold.status === "ready") && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setCancelId(hold.id)}
                        disabled={actionLoading === hold.id}
                      >
                        <XCircle className="w-3 h-3 mr-1" /> Cancel
                      </Button>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {holds && holds.totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 pt-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {holds.page} of {holds.totalPages}
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= holds.totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </PageContainer>

      {/* Cancel confirmation */}
      <Dialog open={!!cancelId} onOpenChange={() => setCancelId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Hold</DialogTitle>
            <DialogDescription>Are you sure you want to cancel this hold?</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end pt-2">
            <Button
              variant="outline"
              onClick={() => setCancelId(null)}
              disabled={actionLoading === cancelId}
            >
              Keep Hold
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancel}
              disabled={actionLoading === cancelId}
              className="gap-2"
            >
              {actionLoading === cancelId ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Cancel Hold
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
};

export default HoldsPage;
